import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import AddModeratorIcon from '@mui/icons-material/AddModerator';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import CancelIcon from '@mui/icons-material/Close';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import InsertLinkOutlinedIcon from '@mui/icons-material/InsertLinkOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import PersonPinOutlinedIcon from '@mui/icons-material/PersonPinOutlined';
import SaveIcon from '@mui/icons-material/Save';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import TextFormatOutlinedIcon from '@mui/icons-material/TextFormatOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import WatchLaterOutlinedIcon from '@mui/icons-material/WatchLaterOutlined';
import WavesOutlinedIcon from '@mui/icons-material/WavesOutlined';
import { Button, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  DataGridPremium as DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridColumnVisibilityModel,
  GridRenderCellParams,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowParams,
  GridRowsProp,
  GridToolbarContainer,
  GridValueFormatterParams,
  GridValueGetterParams,
} from '@mui/x-data-grid-premium';
import { formatCurrency } from 'helpers';
import {
  EstateBalanceSheetAsset,
  EstateBalanceSheetAssetAllocateTo,
  EstateBalanceSheetAssetCategory,
  EstateBalanceSheetAssetColumns,
  EstateBalanceSheetAssetDistributionType,
  EstateBalanceSheetAssetLifetimeHolding,
  EstateBalanceSheetAssetSource,
  EstateBalanceSheetAssetType,
  FeatureSubscriptionType,
  useEstateBalanceSheetAsset_CreateEstateBalanceSheetAssetMutation,
  useEstateBalanceSheetAsset_DeleteEstateBalanceSheetAssetMutation,
  useEstateBalanceSheetAsset_GetEstateBalanceSheetAssetsByEstateBalanceSheetIdQuery,
  useEstateBalanceSheetAsset_UpdateEstateBalanceSheetAssetMutation,
} from 'queries';

import Chip, { ChipDefaultColor } from 'common/chip';
import Modal from 'common/modal';
import Spinner from 'common/spinner';
import { canModifyBalanceSheet } from 'utils/feature-checker';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  container: {
    color: '#1C2541',
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
    marginBottom: '24px',
  },
  dataGrid: {
    border: 0,
    flexGrow: 1,
    borderRadius: '8px',
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
    '& .MuiDataGrid-columnHeadersInner': {
      backgroundColor: '#F9F9FB',
      borderRadius: '8px',
    },
    '& .MuiDataGrid-row': {
      border: '1px solid #ECEEF1',
    },
    '& .MuiDataGrid-cell': {
      border: '1px solid #ECEEF1',
    },
    '& .MuiDataGrid-columnHeaders': {
      borderBottom: 'none',
      borderTop: 'none',
    },
  },
  tableHeaderColumn: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
  },
  tableHeaderColumnLabel: {
    whiteSpace: 'initial',
    fontSize: '14px',
  },
  tableHeaderColumnIcon: {
    marginRight: '8px',
  },
}));

interface AssetsGridRowModel extends GridRowModel<EstateBalanceSheetAsset> {
  isNew: boolean;
}

interface AssetsGridSingleSelectOption {
  value?: string | null;
  label: string;
  color?: string;
}

const initialRows: AssetsGridRowModel[] = [];

export const assetTypeOptions: AssetsGridSingleSelectOption[] = [
  { value: EstateBalanceSheetAssetType.Annuity, label: 'Annuity', color: ChipDefaultColor.BLACK },
  // TODO TD New / Different COLOR?
  { value: EstateBalanceSheetAssetType.Art, label: 'Art', color: ChipDefaultColor.BLACK },
  {
    value: EstateBalanceSheetAssetType.BankAccount,
    label: 'Bank Account(Liquid)',
    color: ChipDefaultColor.PURPLE,
  },
  { value: EstateBalanceSheetAssetType.Boats, label: 'Boats', color: ChipDefaultColor.BLACK },
  { value: EstateBalanceSheetAssetType.Bonds, label: 'Bonds', color: ChipDefaultColor.BLACK },
  {
    value: EstateBalanceSheetAssetType.Businesses,
    label: 'Businesses',
    color: ChipDefaultColor.RED,
  },
  // TODO TD New / Different COLOR?
  {
    value: EstateBalanceSheetAssetType.Cryptocurrency,
    label: 'Cryptocurrency',
    color: ChipDefaultColor.BLACK,
  },
  { value: EstateBalanceSheetAssetType.Cd, label: 'CD', color: ChipDefaultColor.BLACK },
  { value: EstateBalanceSheetAssetType.Dividend, label: 'Dividend', color: ChipDefaultColor.BLACK },
  // TODO TD New / Different COLOR?
  {
    value: EstateBalanceSheetAssetType.Firearm,
    label: 'Cryptocurrency',
    color: ChipDefaultColor.BLACK,
  },
  // TODO TD New / Different COLOR?
  {
    value: EstateBalanceSheetAssetType.ForeignAsset,
    label: 'Foreign Asset',
    color: ChipDefaultColor.BLACK,
  },

  {
    value: EstateBalanceSheetAssetType.IntellectualProperty,
    label: 'Intellectual Property',
    color: ChipDefaultColor.BLACK,
  },
  {
    value: EstateBalanceSheetAssetType.InvestmentAccount,
    label: 'Investment Account',
    color: ChipDefaultColor.YELLOW,
  },
  // TODO TD New / Different COLOR?
  {
    value: EstateBalanceSheetAssetType.Inheritance,
    label: 'Inheritance',
    color: ChipDefaultColor.LIGHT_BLUE,
  },
  // TODO TD New / Different COLOR?
  {
    value: EstateBalanceSheetAssetType.IntellectualProperty,
    label: 'Intellectual Property',
    color: ChipDefaultColor.GRAY,
  },
  // TODO TD New / Different COLOR?
  {
    value: EstateBalanceSheetAssetType.Land,
    label: 'Land',
    color: ChipDefaultColor.BLACK,
  },
  {
    value: EstateBalanceSheetAssetType.LifeInsurance,
    label: 'Life Insurance',
    color: ChipDefaultColor.GRAY,
  },
  {
    value: EstateBalanceSheetAssetType.MotorVehicles,
    label: 'Motor Vehicles',
    color: ChipDefaultColor.BLACK,
  },
  {
    value: EstateBalanceSheetAssetType.OutrightStock,
    label: 'Outright Stock',
    color: ChipDefaultColor.BLUE,
  },
  {
    value: EstateBalanceSheetAssetType.Pension,
    label: 'Pension',
    color: ChipDefaultColor.LIGHT_BLUE,
  },
  // TODO TD New / Different COLOR?
  {
    value: EstateBalanceSheetAssetType.Receivable,
    label: 'Receivable',
    color: ChipDefaultColor.LIGHT_BLUE,
  },
  {
    value: EstateBalanceSheetAssetType.RealProperty,
    label: 'Real Property',
    color: ChipDefaultColor.BLACK,
  },
  {
    value: EstateBalanceSheetAssetType.RetirementAccount,
    label: 'Retirement Account',
    color: ChipDefaultColor.GREEN,
  },
  // TODO TD New / Different COLOR?
  {
    value: EstateBalanceSheetAssetType.SafeDepositBox,
    label: 'Safe Deposit Box',
    color: ChipDefaultColor.PURPLE,
  },
  {
    value: EstateBalanceSheetAssetType.TangiblePersonalProperty,
    label: 'Tangible Personal Property',
    color: ChipDefaultColor.BLACK,
  },
  { value: EstateBalanceSheetAssetType.Trust, label: 'Trust', color: ChipDefaultColor.BLACK },

  { value: EstateBalanceSheetAssetType.Other, label: 'Other', color: ChipDefaultColor.BLACK },
];

export const lifetimeHoldingOptions: AssetsGridSingleSelectOption[] = [
  { value: null, label: '' },
  {
    value: EstateBalanceSheetAssetLifetimeHolding.IrrevTrust,
    label: 'IRREV TRUST',
    color: ChipDefaultColor.ORANGE,
  },
  {
    value: EstateBalanceSheetAssetLifetimeHolding.Jtwros,
    label: 'JTWROS',
    color: ChipDefaultColor.PURPLE,
  },
  {
    value: EstateBalanceSheetAssetLifetimeHolding.Pod,
    label: 'POD',
    color: ChipDefaultColor.BLACK,
  },
  {
    value: EstateBalanceSheetAssetLifetimeHolding.RevTrust,
    label: 'REV TRUST',
    color: ChipDefaultColor.RED,
  },
  {
    value: EstateBalanceSheetAssetLifetimeHolding.Spouse,
    label: 'SPOUSE',
    color: ChipDefaultColor.GREEN,
  },
  {
    value: EstateBalanceSheetAssetLifetimeHolding.Sole,
    label: 'SOLE',
    color: ChipDefaultColor.GREEN,
  },
  {
    value: EstateBalanceSheetAssetLifetimeHolding.Tbe,
    label: 'TBE',
    color: ChipDefaultColor.BLACK,
  },
  {
    value: EstateBalanceSheetAssetLifetimeHolding.Tic,
    label: 'TIC',
    color: ChipDefaultColor.BLACK,
  },

  { value: EstateBalanceSheetAssetLifetimeHolding.Na, label: 'N/A', color: ChipDefaultColor.BLACK },
];

export const distributionTypeOptions: AssetsGridSingleSelectOption[] = [
  { value: null, label: '' },
  {
    value: EstateBalanceSheetAssetDistributionType.NonProbate,
    label: 'Non-Probate',
    color: ChipDefaultColor.PURPLE,
  },
  {
    value: EstateBalanceSheetAssetDistributionType.Probate,
    label: 'Probate',
    color: ChipDefaultColor.BLACK,
  },
];

export const categoryOptions: AssetsGridSingleSelectOption[] = [
  { value: null, label: '' },
  { value: EstateBalanceSheetAssetCategory.Income, label: 'Income', color: ChipDefaultColor.BLACK },
  {
    value: EstateBalanceSheetAssetCategory.Receipt,
    label: 'Receipt',
    color: ChipDefaultColor.PURPLE,
  },
];

export const allocateToOptions: AssetsGridSingleSelectOption[] = [
  { value: null, label: '' },
  {
    value: EstateBalanceSheetAssetAllocateTo.Income,
    label: 'Income',
    color: ChipDefaultColor.BLACK,
  },
  {
    value: EstateBalanceSheetAssetAllocateTo.Principal,
    label: 'Principal',
    color: ChipDefaultColor.PURPLE,
  },
];

export const sourceOptions: AssetsGridSingleSelectOption[] = [
  { value: EstateBalanceSheetAssetSource.BankAccountDiscovery, label: 'Bank Account Discovery' },
  { value: EstateBalanceSheetAssetSource.DecisionVault, label: 'DecisionVault' },
  { value: EstateBalanceSheetAssetSource.EnteredByUser, label: 'Entered by User' },
  {
    value: EstateBalanceSheetAssetSource.LifeInsuranceDiscovery,
    label: 'Life Insurance Discovery',
  },
  { value: EstateBalanceSheetAssetSource.RetirementDiscovery, label: 'Retirement Discovery' },
  { value: EstateBalanceSheetAssetSource.TrusuiteSearch, label: 'Trusuite Search' },
];

interface EditToolbarProps {
  featureSubscriptionType: FeatureSubscriptionType;
  rows: AssetsGridRowModel[];
  setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
  setRowModesModel: (newModel: (oldModel: GridRowModesModel) => GridRowModesModel) => void;
}

function EditToolbar(props: EditToolbarProps) {
  const classes = useStyles();
  const { featureSubscriptionType, rows, setRows, setRowModesModel } = props;

  const handleClick = () => {
    if (!canModifyBalanceSheet(featureSubscriptionType)) {
      showToast({
        message: 'Please upgrade your account to add to the balance sheet.',
        severity: NotificationSeverity.WARNING,
      });
      return;
    }

    setRows((oldRows) => {
      const id = 'new-asset-row-id';

      setRowModesModel((oldModel) => ({
        ...oldModel,
        [id]: { mode: GridRowModes.Edit, fieldToFocus: 'name' },
      }));

      return [
        ...oldRows,
        {
          id,
          assetType: EstateBalanceSheetAssetType.Other,
          assetDescription: '',
          lifetimeHolding: undefined,
          initialValue: undefined,
          initialValueDate: undefined,
          currentValue: undefined,
          currentValueDate: undefined,
          distributionType: undefined,
          nonProbateBeneficiaryName: undefined,
          canIncludeInTaxableEstate: false,
          category: EstateBalanceSheetAssetCategory.Receipt,
          allocateTo: EstateBalanceSheetAssetAllocateTo.Principal,
          notes: undefined,
          source: EstateBalanceSheetAssetSource.EnteredByUser,
          isNew: true,
        },
      ];
    });
  };

  return (
    <GridToolbarContainer>
      <Button
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleClick}
        disabled={!!rows.find((row) => row.isNew)}
        className={classes.container}
      >
        Add new
      </Button>
    </GridToolbarContainer>
  );
}

type Props = {
  featureSubscriptionType: FeatureSubscriptionType;
  hiddenAssetColumns: EstateBalanceSheetAssetColumns[];
  estateBalanceSheetId: string;
  onDataLoaded: (data: EstateBalanceSheetAsset[]) => void;
};

export default function EstateBalanceSheetAssets({
  featureSubscriptionType,
  hiddenAssetColumns,
  estateBalanceSheetId,
  onDataLoaded,
}: Props) {
  const classes = useStyles();

  const { portalId } = useParams<Record<string, string>>();

  const [rows, setRows] = useState<AssetsGridRowModel[]>(initialRows);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [selectedRowToDelete, setSelectedRowToDelete] = useState<GridRowId>();

  const { data: estateBalanceSheetAssetsData, loading: estateBalanceSheetAssetsLoading } =
    useEstateBalanceSheetAsset_GetEstateBalanceSheetAssetsByEstateBalanceSheetIdQuery({
      variables: { input: { estateBalanceSheetId, portalId } },
      onError: (e) =>
        showAlert({
          message: 'Error getting balance sheet assets by balance sheet ID: ' + e.message,
          severity: NotificationSeverity.ERROR,
        }),
    });

  const [createEstateBalanceSheetAsset] =
    useEstateBalanceSheetAsset_CreateEstateBalanceSheetAssetMutation({
      onError: (e) =>
        showAlert({
          message: 'Error creating new balance sheet asset: ' + e.message,
          severity: NotificationSeverity.ERROR,
        }),
    });

  const [updateEstateBalanceSheetAsset] =
    useEstateBalanceSheetAsset_UpdateEstateBalanceSheetAssetMutation({
      onError: (e) =>
        showAlert({
          message: 'Error updating balance sheet asset: ' + e.message,
          severity: NotificationSeverity.ERROR,
        }),
    });

  const [deleteEstateBalanceSheetAsset] =
    useEstateBalanceSheetAsset_DeleteEstateBalanceSheetAssetMutation({
      onError: (e) =>
        showAlert({
          message: 'Error deleting balance sheet asset: ' + e.message,
          severity: NotificationSeverity.ERROR,
        }),
    });

  useEffect(() => {
    if (rows.length === 0 && estateBalanceSheetAssetsData?.data) {
      setRows(
        estateBalanceSheetAssetsData.data.map((asset) => {
          return { ...asset, isNew: false };
        }),
      );
    }
  }, [estateBalanceSheetAssetsData, rows.length]);

  useEffect(() => {
    if (estateBalanceSheetAssetsData?.data) {
      onDataLoaded(
        estateBalanceSheetAssetsData.data.map((asset) => ({
          ...asset,
          isNew: false,
        })),
      );
    }
  }, [estateBalanceSheetAssetsData, onDataLoaded, rows]);

  if (estateBalanceSheetAssetsLoading || !estateBalanceSheetAssetsData) {
    return <Spinner />;
  }

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = () => {
    const rowToDelete = rows.find((row) => row.id === selectedRowToDelete);
    if (rowToDelete && !rowToDelete.isNew) {
      deleteEstateBalanceSheetAsset({
        onCompleted: () => {
          setRows(rows.filter((row) => row.id !== rowToDelete.id));
        },
        variables: { input: { id: rowToDelete.id, portalId } },
      });
    } else {
      setRows(rows.filter((row) => row.id !== selectedRowToDelete));
    }
    setSelectedRowToDelete(undefined);
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow?.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow: AssetsGridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };

    const {
      id,
      assetType,
      assetDescription,
      lifetimeHolding,
      initialValue,
      initialValueDate,
      currentValue,
      currentValueDate,
      distributionType,
      nonProbateBeneficiaryName,
      canIncludeInTaxableEstate,
      category,
      allocateTo,
      notes,
      source,
    } = newRow;

    if (newRow.isNew) {
      createEstateBalanceSheetAsset({
        onCompleted: ({ data: newEstateBalanceSheetAsset }) => {
          if (newEstateBalanceSheetAsset) {
            const updatedRows = rows.map((row) =>
              row.id === newRow.id ? { ...newEstateBalanceSheetAsset, isNew: false } : row,
            );

            setRows(updatedRows);
            onDataLoaded(updatedRows);
          }
        },
        variables: {
          input: {
            estateBalanceSheetId,
            portalId,
            assetType,
            assetDescription,
            lifetimeHolding,
            initialValue,
            initialValueDate,
            currentValue,
            currentValueDate,
            distributionType,
            nonProbateBeneficiaryName,
            canIncludeInTaxableEstate,
            category,
            allocateTo,
            notes,
            source,
          },
        },
      });
    } else {
      updateEstateBalanceSheetAsset({
        onCompleted: ({ data: newEstateBalanceSheetAsset }) => {
          setRows(
            rows.map((row) =>
              row.id === newRow.id ? { ...newEstateBalanceSheetAsset, isNew: false } : row,
            ),
          );
        },
        variables: {
          input: {
            id,
            portalId,
            assetType,
            assetDescription,
            lifetimeHolding,
            initialValue,
            initialValueDate,
            currentValue,
            currentValueDate,
            distributionType,
            nonProbateBeneficiaryName,
            canIncludeInTaxableEstate,
            category,
            allocateTo,
            notes,
            source,
          },
        },
      });
    }
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns: GridColDef[] = [
    {
      field: EstateBalanceSheetAssetColumns.AssetType,
      minWidth: 150,
      editable: true,
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <CategoryOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Asset Type</Typography>
        </div>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const value: string = params.value;
        const colDef: any = params.colDef;
        const valueOption: AssetsGridSingleSelectOption | undefined = colDef.valueOptions.find(
          (valueOption: AssetsGridSingleSelectOption) => valueOption.value === value,
        );
        if (!value) {
          return <></>;
        } else if (valueOption) {
          return <Chip label={valueOption.label} color={valueOption.color} />;
        } else {
          return <Chip label={value} />;
        }
      },
      type: 'singleSelect',
      valueOptions: assetTypeOptions,
    },
    {
      field: EstateBalanceSheetAssetColumns.AssetDescription,
      minWidth: 200,
      editable: true,
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <TextFormatOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Asset Description</Typography>
        </div>
      ),
    },
    {
      field: EstateBalanceSheetAssetColumns.LifetimeHolding,
      minWidth: 200,
      editable: true,
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <PersonPinOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>
            How was this held during lifetime?
          </Typography>
        </div>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const value: string = params.value;
        const colDef: any = params.colDef;
        const valueOption: AssetsGridSingleSelectOption | undefined = colDef.valueOptions.find(
          (valueOption: AssetsGridSingleSelectOption) => valueOption.value === value,
        );
        if (!value) {
          return <></>;
        } else if (valueOption) {
          return <Chip label={valueOption.label} color={valueOption.color} />;
        } else {
          return <Chip label={value} />;
        }
      },
      type: 'singleSelect',
      valueOptions: lifetimeHoldingOptions,
    },
    {
      field: EstateBalanceSheetAssetColumns.InitialValue,
      minWidth: 120,
      editable: true,
      type: 'number',
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <MonetizationOnOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Value</Typography>
        </div>
      ),
      valueFormatter: ({ value }) => (value ? formatCurrency(`${value * 100.0}`) : undefined),
    },
    {
      field: EstateBalanceSheetAssetColumns.InitialValueDate,
      minWidth: 150,
      editable: true,
      type: 'date',
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <WatchLaterOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Initial Value Date/DOD</Typography>
        </div>
      ),
      valueGetter: (params: GridValueGetterParams) => {
        const row: EstateBalanceSheetAsset = params.row;
        return row.initialValueDate ? new Date(row.initialValueDate) : undefined;
      },
    },
    {
      field: EstateBalanceSheetAssetColumns.CurrentValue,
      minWidth: 150,
      editable: true,
      type: 'number',
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <MonetizationOnOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>
            Post Death Appreciation
          </Typography>
        </div>
      ),
      valueFormatter: ({ value }: GridValueFormatterParams) =>
        value ? formatCurrency(`${value * 100.0}`) : undefined,
    },
    {
      field: EstateBalanceSheetAssetColumns.CurrentValueDate,
      minWidth: 150,
      editable: true,
      type: 'date',
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <WatchLaterOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Current Value Date</Typography>
        </div>
      ),
      valueGetter: (params: GridValueGetterParams) => {
        const row: EstateBalanceSheetAsset = params.row;
        return row.currentValueDate ? new Date(row.currentValueDate) : undefined;
      },
    },
    {
      field: EstateBalanceSheetAssetColumns.DistributionType,
      minWidth: 150,
      editable: true,
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <VerifiedUserOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Distribution Type</Typography>
        </div>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const value: string = params.value;
        const colDef: any = params.colDef;
        const valueOption: AssetsGridSingleSelectOption | undefined = colDef.valueOptions.find(
          (valueOption: AssetsGridSingleSelectOption) => valueOption.value === value,
        );
        if (!value) {
          return <></>;
        } else if (valueOption) {
          return <Chip label={valueOption.label} color={valueOption.color} />;
        } else {
          return <Chip label={value} />;
        }
      },
      type: 'singleSelect',
      valueOptions: distributionTypeOptions,
    },
    {
      field: EstateBalanceSheetAssetColumns.NonProbateBeneficiaryName,
      minWidth: 300,
      editable: true,
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <VerifiedUserOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>
            Name of Beneficiary(ies) if Non-Probate
          </Typography>
        </div>
      ),
    },
    {
      field: EstateBalanceSheetAssetColumns.CanIncludeInTaxableEstate,
      minWidth: 150,
      editable: true,
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <VerifiedUserOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>
            Include in taxable estate?
          </Typography>
        </div>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const value: string = params.value;
        const colDef: any = params.colDef;
        const valueOption: AssetsGridSingleSelectOption | undefined = colDef.valueOptions.find(
          (valueOption: AssetsGridSingleSelectOption) => valueOption.value === value,
        );
        if (!valueOption && value === undefined) {
          return <></>;
        } else if (valueOption) {
          return <Chip label={valueOption.label} color={valueOption.color} />;
        } else {
          return <Chip label={value} />;
        }
      },
      type: 'singleSelect',
      valueOptions: [
        { value: true, label: 'Yes', color: ChipDefaultColor.PURPLE },
        { value: false, label: 'No', color: ChipDefaultColor.BLACK },
      ],
    },
    {
      field: EstateBalanceSheetAssetColumns.Category,
      minWidth: 150,
      editable: true,
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <WavesOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Category</Typography>
        </div>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const value: string = params.value;
        const colDef: any = params.colDef;
        const valueOption: AssetsGridSingleSelectOption | undefined = colDef.valueOptions.find(
          (valueOption: AssetsGridSingleSelectOption) => valueOption.value === value,
        );
        if (!value) {
          return <></>;
        } else if (valueOption) {
          return <Chip label={valueOption.label} color={valueOption.color} />;
        } else {
          return <Chip label={value} />;
        }
      },
      type: 'singleSelect',
      valueOptions: categoryOptions,
    },
    {
      field: EstateBalanceSheetAssetColumns.AllocateTo,
      minWidth: 150,
      editable: true,
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <AddModeratorIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Allocate To</Typography>
        </div>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const value: string = params.value;
        const colDef: any = params.colDef;
        const valueOption: AssetsGridSingleSelectOption | undefined = colDef.valueOptions.find(
          (valueOption: AssetsGridSingleSelectOption) => valueOption.value === value,
        );
        if (!value) {
          return <></>;
        } else if (valueOption) {
          return <Chip label={valueOption.label} color={valueOption.color} />;
        } else {
          return <Chip label={value} />;
        }
      },
      type: 'singleSelect',
      valueOptions: allocateToOptions,
    },
    {
      field: EstateBalanceSheetAssetColumns.Notes,
      minWidth: 200,
      editable: true,
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <CreateOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Comment / Notes</Typography>
        </div>
      ),
    },
    {
      field: EstateBalanceSheetAssetColumns.Source,
      minWidth: 150,
      editable: true,
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <InsertLinkOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Source</Typography>
        </div>
      ),
      type: 'singleSelect',
      valueOptions: sourceOptions,
    },
    {
      field: EstateBalanceSheetAssetColumns.Actions,
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      renderHeader: () => (
        <div className={classes.tableHeaderColumn}>
          <SettingsOutlinedIcon className={classes.tableHeaderColumnIcon} fontSize="small" />
          <Typography className={classes.tableHeaderColumnLabel}>Actions</Typography>
        </div>
      ),
      getActions: ({ id }: GridRowParams) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key={`${id}-0`}
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              key={`${id}-1`}
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            key={`${id}-2`}
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            key={`${id}-3`}
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => setSelectedRowToDelete(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  const columnVisibilityModel = hiddenAssetColumns.reduce(
    (acc, column) => ({ ...acc, [column]: false }),
    {},
  ) as GridColumnVisibilityModel;

  return (
    <div className={classes.container}>
      <Modal
        setShowModal={() => setSelectedRowToDelete(undefined)}
        onActionClick={handleDeleteClick}
        onExitClick={() => setSelectedRowToDelete(undefined)}
        showModal={Boolean(selectedRowToDelete)}
        title=""
        content={<Typography>Are you sure? This will permanently delete this row.</Typography>}
        actionVerb="Delete"
      />
      <Typography variant="h1" className={classes.title}>
        Assets
      </Typography>
      <DataGrid
        className={classes.dataGrid}
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        processRowUpdate={processRowUpdate}
        getRowHeight={() => 'auto'}
        autoHeight={true}
        columnVisibilityModel={columnVisibilityModel}
        slots={{
          toolbar: (props) => (
            <EditToolbar featureSubscriptionType={featureSubscriptionType} rows={rows} {...props} />
          ),
        }}
        slotProps={{
          toolbar: { setRows, setRowModesModel },
        }}
      />
    </div>
  );
}
